import { useState, useCallback } from 'react';
import { getToken } from 'firebase/messaging';
import { getMessagingInstance, initializeFirebase } from '../../config/firebase';
import { useMutation } from '@apollo/client';
import { UPDATE_FCM_DEVICE } from '../../graphQLDefs/Notifications/mutations';
import { getOrCreateDeviceId } from '../../utils/deviceUtils';

// Extend Window interface for FCM debug logs (used in E2E tests)
declare global {
  interface Window {
    __fcmLogs?: string[];
  }
}

export const useBrowserNotificationPermission = () => {
  const [permission, setPermission] = useState<NotificationPermission>(
    'Notification' in window ? Notification.permission : 'denied'
  );
  const [fcmToken, setFcmToken] = useState<string | null>(null);
  const [updateDevice] = useMutation(UPDATE_FCM_DEVICE);

  const requestPermission = useCallback(async (): Promise<NotificationPermission> => {
    if (!('Notification' in window)) {
      console.warn('Browser does not support notifications');
      return 'denied';
    }

    if (Notification.permission === 'granted') {
      return 'granted';
    }

    const result = await Notification.requestPermission();
    setPermission(result);
    return result;
  }, []);

  const getFCMToken = useCallback(async (): Promise<string | null> => {
    let messaging = getMessagingInstance();
    if (!window.__fcmLogs) {
      window.__fcmLogs = [];
    }

    if (!messaging) {
      try {
        await initializeFirebase();
        messaging = getMessagingInstance();
      } catch (err) {
        console.warn('[FCM] Error attempting to initialize Firebase:', err);
      }
    }

    if (!messaging) {
      console.error(
        '[FCM] Messaging not initialized - Firebase may not be initialized or not supported'
      );
      window.__fcmLogs.push('ERROR: Messaging not initialized');
      return null;
    }

    console.log('[FCM] Messaging instance obtained, getting token...');
    window.__fcmLogs.push('Messaging instance OK');

    try {
      const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
      if (!vapidKey) {
        console.error('[FCM] VAPID key not configured');
        window.__fcmLogs.push('ERROR: VAPID key missing');
        return null;
      }

      console.log('[FCM] VAPID key present, requesting token from Firebase...');
      window.__fcmLogs.push('VAPID key OK, requesting token');

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;
      console.log('[FCM] Service worker ready:', registration);
      window.__fcmLogs.push('Service worker ready');

      const token = await getToken(messaging, {
        vapidKey,
        serviceWorkerRegistration: registration,
      });
      setFcmToken(token);

      // Log for E2E tests
      console.log('[FCM] FCM token retrieved successfully:', token.substring(0, 20) + '...');
      window.__fcmLogs.push('FCM token obtained');
      localStorage.setItem('fcm_token', token);

      return token;
    } catch (error) {
      console.error('[FCM] Failed to get FCM token:', error);
      window.__fcmLogs.push(`ERROR getting token: ${error}`);
      return null;
    }
  }, []);

  /**
   * Updates the device with FCM token
   * Note: Device is already registered during sign-in, this just adds the FCM token
   */
  const updateFCMToken = useCallback(
    async (token: string) => {
      try {
        const deviceId = getOrCreateDeviceId();

        await updateDevice({
          variables: {
            input: {
              deviceId,
              fcmToken: token,
            },
          },
          // Suppress error toast for FCM updates - it's non-critical
          // and expected to fail in local development environments
          context: {
            suppressErrorToast: true,
          },
        });

        // Log for E2E tests
        console.log('[FCM] Device FCM token updated successfully');
        if (!window.__fcmLogs) {
          window.__fcmLogs = [];
        }
        window.__fcmLogs.push('FCM token updated on device');
      } catch (error) {
        // Log as warning - FCM update failures are non-critical
        console.warn('[FCM] Failed to update device FCM token (non-critical):', error);
        if (!window.__fcmLogs) {
          window.__fcmLogs = [];
        }
        window.__fcmLogs.push(`FCM token update failed (non-critical): ${error}`);
        // Don't throw - FCM failures shouldn't break the app
      }
    },
    [updateDevice]
  );

  const initializeBrowserNotifications = useCallback(async () => {
    console.log('[FCM] Starting browser notifications initialization...');
    if (!window.__fcmLogs) {
      window.__fcmLogs = [];
    }
    window.__fcmLogs.push('Starting initialization');

    try {
      const permissionResult = await requestPermission();
      console.log('[FCM] Permission result:', permissionResult);
      window.__fcmLogs.push(`Permission: ${permissionResult}`);

      if (permissionResult === 'granted') {
        const token = await getFCMToken();
        console.log('[FCM] Token obtained:', token ? 'Yes' : 'No');

        if (token) {
          await updateFCMToken(token);
          console.log('[FCM] Registration complete');
        } else {
          console.warn('[FCM] No token obtained');
          window.__fcmLogs.push('No token obtained');
        }
      } else {
        console.warn('[FCM] Permission not granted');
        window.__fcmLogs.push(`Permission denied: ${permissionResult}`);
      }
    } catch (error) {
      console.error('[FCM] Initialization error:', error);
      window.__fcmLogs.push(`Error: ${error}`);
      throw error;
    }
  }, [requestPermission, getFCMToken, updateFCMToken]);

  return {
    permission,
    fcmToken,
    requestPermission,
    getFCMToken,
    updateFCMToken,
    initializeBrowserNotifications,
  };
};
